import React from 'react';
import axios from 'axios';

import { ACTION_TYPE_DESCRIPTOR } from '~/components/AiChat/SideBarDialog/Actions/Action/ActionTypes';
import { ReviewAndExecuteFooter } from '~/components/AiChat/SideBarDialog/Actions/Action/ReviewAndExecute/ReviewAndExecuteFooter';
import type { AiAction, AiChatExchangesAndReferencesApiResponse } from '~/components/AiChat/types';
import { AI_ACTION_TYPES } from '~/components/AiChat/types';
import { findActionExposure } from '~/components/AiChat/utils/actionExposure';
import { useClaim } from '~/components/ClaimContainer';
import { useCms } from '~/components/hooks/useCms';
import type { ExecuteClaimActionParams, IgnoreActionParams } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';

export interface ActionCardModel {
  taskType: string | undefined;
  title: string;
  relatedExposureLabel: string | undefined;
  isDone: boolean;
  InnerComponent: React.ElementType | null;
  ActionFooterComponent: React.ElementType | null | undefined;
  executeAction: () => Promise<void>;
  unexecuteAction: () => Promise<void>;
  dismissAction: (reason: string) => Promise<void>;
}

/**
 * Shared action-card logic: descriptor resolution (title / inner / footer components) and
 * the execute / unexecute / ignore operations against the actions API.
 */
export const useActionCard = (action: AiAction, onActionUpdate: () => Promise<void>): ActionCardModel => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { userOrganization } = useCms();

  if (!action?.task_data) reportErrorInProductionOrThrow(new Error('Action task_data is missing'));

  const taskType = action.task_data?.task_type;
  const [title, setTitle] = React.useState<string>('');
  const [InnerComponent, setInnerComponent] = React.useState<React.ElementType | null>(null);
  const [ActionFooterComponent, setActionFooterComponent] = React.useState<React.ElementType | null | undefined>();
  const [isDone, setIsDone] = React.useState(action.is_executed);

  const relatedExposureLabel = React.useMemo(
    () => findActionExposure(action, claim?.exposures)?.label_text,
    [action, claim]
  );

  const performActionOperation = React.useCallback(
    async (
      operationType: 'execute' | 'execute_action_tool' | 'unexecute' | 'ignore',
      shouldSetDone = false,
      shouldSetNotDone = false,
      body?: IgnoreActionParams | ExecuteClaimActionParams
    ) => {
      try {
        if (shouldSetDone) {
          setIsDone(true);
        }
        if (shouldSetNotDone) {
          setIsDone(false);
        }

        await axios.post<AiChatExchangesAndReferencesApiResponse>(
          `/api/v1/claims/${claim.id}/actions/${action.action_id}/${operationType}`,
          body
        );
      } catch (error) {
        await reportAxiosError(error);
        if (shouldSetDone) {
          setIsDone(false);
        }
        throw error;
      } finally {
        await onActionUpdate();
        await onAsyncClaimUpdate();
      }
    },
    [onActionUpdate, onAsyncClaimUpdate, claim, action]
  );

  // The unified dynamic claim action always executes through the configured-execution
  // (`/execute_action_tool`) path: it has no legacy `/execute` executor, so it is not gated on
  // the feature flag. Legacy per-type actions stay flag-gated as before.
  const usesConfiguredExecution = React.useMemo(
    () =>
      taskType === AI_ACTION_TYPES.DYNAMIC_CLAIM_ACTION ||
      (!!taskType &&
        !!ACTION_TYPE_DESCRIPTOR[taskType]?.usesConfiguredExecution &&
        isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CLAIM_ACTIONS_CONFIGURED_EXECUTION)),
    [taskType, userOrganization]
  );

  // The server refuses the execute when the action has changed since the revision this card shows.
  const executeAction = React.useCallback(
    () =>
      performActionOperation(usesConfiguredExecution ? 'execute_action_tool' : 'execute', true, false, {
        expected_revision: action.revision,
      }),
    [performActionOperation, usesConfiguredExecution, action.revision]
  );

  const unexecuteAction = React.useCallback(
    () => performActionOperation('unexecute', false, true),
    [performActionOperation]
  );

  // The reason is a parameter rather than an option so that every caller — this card today, the
  // Conductor Hub's cards next — has to collect one. `IgnoreActionParams.reason` is what the AI
  // service stores as `status_change.reason`.
  const dismissAction = React.useCallback(
    (reason: string) => performActionOperation('ignore', false, false, { reason }),
    [performActionOperation]
  );

  React.useEffect(() => {
    if (taskType) {
      const descriptor = ACTION_TYPE_DESCRIPTOR[taskType];
      if (!descriptor) {
        reportErrorInProductionOrThrow(new Error(`Invalid action task data type ${action.task_data?.task_type}`));
        return;
      }

      setTitle(() => (typeof descriptor.title === 'function' ? descriptor.title(action) : descriptor.title));
      setInnerComponent(() => descriptor.InnerActionComponent);
      setActionFooterComponent(() =>
        usesConfiguredExecution ? ReviewAndExecuteFooter : descriptor.ActionFooterComponent
      );
    }
  }, [taskType, action, usesConfiguredExecution]);

  return {
    taskType,
    title,
    relatedExposureLabel,
    isDone,
    InnerComponent,
    ActionFooterComponent,
    executeAction,
    unexecuteAction,
    dismissAction,
  };
};
